import { multicallRetryUniversal } from '@1delta/providers'
import {
  StructuredOraclePrices,
  USDPriceMap,
  ParseContext,
  TokenListMap,
  OraclePriceEntry,
  ChainDiagnostic,
  TrackerDiagnostic,
  OracleDiagnostics,
  OraclePricesResult,
  StaleFeedEntry,
} from './types'
import {
  aaveFetcher,
  morphoFetcher,
  midnightFetcher,
  exactlyFetcher,
  getExactlyAbi,
  flyingTulipFetcher,
  getFlyingTulipAbi,
  termFetcher,
  getTermAbi,
  liquityFetcher,
  getLiquityAbi,
  riverFetcher,
  dssFetcher,
  curvanceFetcher,
  resupplyFetcher,
  getRiverAbi,
  inverseFetcher,
  getInverseAbi,
  tellerFetcher,
  fraxlendFetcher,
  termMaxFetcher,
  getTellerAbi,
  getFraxlendAbi,
  getTermMaxAbi,
  compoundV2Fetcher,
  compoundV3Fetcher,
  listaFetcher,
  llamaLendFetcher,
  getLlamaLendAbi,
  eulerV2Fetcher,
  aaveV4Fetcher,
  siloV2Fetcher,
  siloV3Fetcher,
  fluidFetcher,
  gearboxV3Fetcher,
  dolomiteFetcher,
  fetchMorphoGraphQLPrices,
  morphoApiAvailable,
  morphoApiIncomplete,
  type MorphoMarketOverrides,
  type ListaMarketOverrides,
  type LlamaLendMarketOverrides,
} from './fetchers'
import { AaveOracleAbi } from '../../abis/oracle/AaveOracle'
import { ProxyOracleAbi } from '../../abis/oracle/ProxyOracle'
import { CompoundV2OracleAbi } from './fetchers/compoundV2'
import { ChainlinkAggregatorAbi } from './fetchers/compoundV3'
import { getEulerV2Abi } from './fetchers/euler'
import { getAaveV4Abi } from './fetchers/aaveV4'
import { getSiloV2Abi } from './fetchers/siloV2'
import { getSiloV3Abi } from './fetchers/siloV3'
import { getFluidAbi } from './fetchers/fluid'
import { getGearboxV3Abi } from './fetchers/gearbox'
import { getDolomiteAbi } from './fetchers/dolomite'
import { fetchSiloV2GraphQLPrices } from './fetchers/siloV2Graphql'
import { fetchSiloV3GraphQLPrices } from './fetchers/siloV3Graphql'
import { SILO_API_SUPPORTED_CHAIN_IDS } from '../../lending/public-data/silo-v2/fetchPublic'
import { morphoIncludesUnlisted } from '../../lending/public-data/morpho/unlisted'
import {
  fetchMorphoMysticPrices,
  mysticPricesAvailable,
} from './fetchers/fetchMorphoMysticPrices'
import { probeAaveFeedTimestamps, type FeedTimestampMap } from './feedStaleness'
import { resolveDerivation } from './derivation'
import type { PriceDerivation } from './types'
import {
  termMaxConfigByChain,
  curvanceLendersByChain,
  resupplyLendersByChain,
} from '@1delta/data-sdk'
import { fetchTermMaxMarkets } from '../../lending/public-data/termmax/fetchPublic'
import { fetchCurvanceMarkets } from '../../lending/public-data/curvance/fetchPublic'
import { getCurvanceAssetRoster } from './fetchers/curvanceRoster'
import { fetchResupplyMarkets } from '../../lending/public-data/resupply/fetchPublic'
import { getResupplyPairRoster } from './fetchers/resupplyRoster'

/**
 * Token list type expected by this function
 * Token decimals are read from list[chainId].list[address].decimals
 */
export type TokenListInput = {
  [chainId: string]: TokenListMap
}

/**
 * Options for fetchOraclePrices with market overrides
 */
export interface FetchOraclePricesOptions {
  /** Optional RPC URL overrides per chain */
  rpcOverrides?: { [chainId: string]: string[] }
  /** Token lists with decimals and asset groups for price resolution */
  lists?: TokenListInput
  /** Number of retries for RPC calls */
  retries?: number
  /** Optional batch size for multicall, per chain */
  batchSize?: { [chainId: string]: number }
  /** Whether to allow individual call failures (default: true) */
  allowFailure?: boolean
  /** Optional base USD prices to use for deriving other prices */
  basePrices?: USDPriceMap
  /** Custom Morpho markets to add beyond JSON data */
  morphoMarketOverrides?: MorphoMarketOverrides
  /** Custom Lista markets to add beyond JSON data */
  listaMarketOverrides?: ListaMarketOverrides
  /**
   * Chainlink staleness threshold in seconds (default: 3600 = 1 hour).
   * Feeds with updatedAt older than this are flagged in diagnostics.
   * Set to 0 to disable staleness checking (also skips the AAVE feed probe).
   *
   * This is a REPORTING threshold only — it is intentionally tighter than the
   * `staleRejectSeconds` used by `selectAssetGroupPrices` to demote a price,
   * because a healthy 24h-heartbeat stablecoin feed is routinely hours old.
   */
  stalenessThresholdSeconds?: number
  /**
   * Resolve AAVE-family feed sources and read their `updatedAt` (default true
   * when staleness checking is on). Costs two extra multicalls per chain, run
   * concurrently with the price groups. Without it, chains whose only price
   * source is an AAVE fork — Avalanche has no Compound V3 deployment — get no
   * staleness coverage at all.
   */
  probeFeedStaleness?: boolean
  /**
   * Only run these fetcher groups. Useful for debugging individual protocols.
   * Values: 'aave', 'compoundV2', 'compoundV3', 'lista', 'llamalend',
   * 'eulerV2', 'aaveV4', 'morpho', 'midnight', 'exactly', 'term', 'liquity',
   * 'river', 'teller', 'siloV2', 'siloV3', 'fluid', 'curvance', 'resupply'.
   * If omitted, all fetchers run.
   */
  onlyFetchers?: string[]
}

/**
 * A group of calls from one fetcher, with its ABI and parse function.
 * Each group is executed as an independent multicall.
 */
interface FetcherGroup {
  fetcherName: string
  calls: any[]
  abi: readonly any[]
  /**
   * Derivation class of every entry this fetcher produces, unless the parser
   * sets one per entry (Euler does — its unit of account varies per vault).
   * Declared here rather than at ~40 `entries.push` sites so the whole
   * quality classification of the pipeline is reviewable in one screen.
   */
  derivation: PriceDerivation
  trackers: {
    lender: string
    meta: any
    offset: number
    count: number
    parse: (data: any[], meta: any, context: ParseContext) => OraclePriceEntry[]
  }[]
}

/** Count how many entries in a data slice are '0x' (failed calls) */
function countFailures(data: any[], offset: number, count: number): number {
  let failures = 0
  for (let i = offset; i < offset + count; i++) {
    if (data[i] === '0x' || data[i] === undefined) failures++
  }
  return failures
}

/**
 * Safely call a fetcher's getCalls(), returning empty on error
 */
function safeGetCalls<T>(
  fetcherName: string,
  fn: () => T[],
  errors: { [fetcher: string]: string },
): T[] {
  try {
    return fn()
  } catch (e) {
    errors[fetcherName] = e instanceof Error ? e.message : String(e)
    return []
  }
}

/** True for a 20-byte hex address; checksum not enforced (the ABI encoder does not either). */
const isCallTarget = (a: unknown): boolean =>
  typeof a === 'string' && /^0x[0-9a-fA-F]{40}$/.test(a)

/**
 * Build a FetcherGroup from fetcher results.
 * Groups all calls for a single fetcher type into one multicall batch.
 *
 * A fetcher result whose calls target something that is not an address is
 * DROPPED here, whole, and recorded in `getCallsErrors` as
 * `<fetcher>:<lender>` — never batched. viem refuses to encode such a target
 * (`InvalidAddressError`) before any request is sent, and that one throw
 * takes every other call in the SAME batch with it: lender-metadata
 * publishing `'0x'` as the Compound V2 oracle of USDFI and ApeSwap on BNB
 * cost Venus, Cream and Segment every price on chain 56 (110 entries) on
 * every cycle, while the multicall ladder replayed the throw on 16
 * providers. The registry value is the defect; this is the containment, and
 * the diagnostic is what makes the defect visible instead of silent.
 * Dropped as a unit rather than per call because `parse` reads `meta[i]`
 * beside `data[i]` — removing one call would shift every result after it.
 */
function buildGroup(
  fetcherName: string,
  results: any[],
  parse: any,
  abi: readonly any[],
  derivation: PriceDerivation,
  getCallsErrors?: { [fetcher: string]: string },
): FetcherGroup {
  const group: FetcherGroup = {
    fetcherName,
    calls: [],
    abi,
    derivation,
    trackers: [],
  }
  for (const fr of results) {
    const bad = (fr.calls as any[]).filter((c) => !isCallTarget(c?.address))
    if (bad.length > 0) {
      const sample = bad[0]
      const msg =
        `${bad.length}/${fr.calls.length} calls target an invalid address ` +
        `(${JSON.stringify(sample?.address)} ${sample?.name ?? '?'}) — lender dropped from the batch; ` +
        'fix the registry entry (lender-metadata), this is not an RPC failure'
      if (getCallsErrors) getCallsErrors[`${fetcherName}:${fr.lender}`] = msg
      console.warn(`[oracle-prices] ${fetcherName} ${fr.lender}: ${msg}`)
      continue
    }
    group.trackers.push({
      lender: fr.lender,
      meta: fr.meta,
      offset: group.calls.length,
      count: fr.calls.length,
      parse,
    })
    group.calls.push(...fr.calls)
  }
  return group
}

/** Check if a result is a failure sentinel */
function isFailed(r: any): boolean {
  return r === '0x' || r === undefined
}

/**
 * Execute a single fetcher group's multicall and return the raw results.
 * Returns '0x' for all calls if the multicall fails entirely.
 *
 * After the initial pass, any calls that returned '0x' are retried on a
 * different RPC (up to `failRetries` rounds). This recovers from transient
 * RPC failures that viem's allowFailure=true silently absorbs, while
 * legitimate contract reverts stay '0x' because they fail consistently.
 */
async function executeGroup(
  group: FetcherGroup,
  chainId: string,
  chainBatchSize: number | undefined,
  retries: number,
  allowFailure: boolean,
  rpcOverrides: { [chainId: string]: string[] } | undefined,
  failRetries = 2,
): Promise<{ results: any[]; error?: string }> {
  if (group.calls.length === 0) {
    return { results: [] }
  }

  try {
    const results = await multicallRetryUniversal({
      chain: chainId,
      calls: group.calls,
      abi: group.abi as any,
      batchSize: chainBatchSize,
      maxRetries: retries,
      providerId: undefined,
      allowFailure,
      overrdies: rpcOverrides,
      logErrors: true,
    })

    // Retry only the failed indices on a different RPC.
    // Legitimate reverts will keep failing; RPC flakiness will recover.
    let failedIndices = results
      .map((r, i) => (isFailed(r) ? i : -1))
      .filter((i) => i >= 0)

    for (
      let round = 0;
      round < failRetries && failedIndices.length > 0;
      round++
    ) {
      const retryCalls = failedIndices.map((i) => group.calls[i])
      try {
        const retryResults = await multicallRetryUniversal({
          chain: chainId,
          calls: retryCalls,
          abi: group.abi as any,
          batchSize: chainBatchSize,
          maxRetries: retries,
          providerId: round + 1, // rotate to next RPC provider
          allowFailure,
          overrdies: rpcOverrides,
          logErrors: true,
        })
        // Merge recovered results back
        const stillFailed: number[] = []
        for (let j = 0; j < failedIndices.length; j++) {
          if (!isFailed(retryResults[j])) {
            results[failedIndices[j]] = retryResults[j]
          } else {
            stillFailed.push(failedIndices[j])
          }
        }
        failedIndices = stillFailed
      } catch {
        // Retry round failed entirely — move on
        break
      }
    }

    // Detect total wipeout
    const failCount = results.filter((r: any) => isFailed(r)).length
    const allFailed = failCount === results.length && results.length > 0
    return {
      results,
      error: allFailed
        ? `All ${group.calls.length} calls returned 0x (${group.fetcherName})`
        : undefined,
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    return {
      results: Array(group.calls.length).fill('0x'),
      error: `multicall threw: ${msg.slice(0, 200)}`,
    }
  }
}

/**
 * Fetches oracle prices from AAVE, Morpho, Lista, Compound V2/V3, and Euler V2 protocols.
 * Returns structured data and a diagnostics report.
 *
 * Architecture:
 * - Each fetcher group (AAVE, CompoundV2, CompoundV3, Lista, Euler, Morpho)
 *   runs as an independent multicall in parallel — heavy protocols don't block others
 * - Parsing happens afterward in dependency order (AAVE sources → others → Morpho)
 * - Chain-level isolation via Promise.allSettled
 * - Fetcher/parser-level isolation via try-catch
 */
export async function fetchOraclePrices(
  chainIds: string[],
  rpcOverrides?: { [chainId: string]: string[] },
  lists: TokenListInput = {},
  retries = 3,
  batchSize: { [chainId: string]: number } | undefined = undefined,
  allowFailure = true,
  basePrices: USDPriceMap = {},
  morphoMarketOverrides?: MorphoMarketOverrides,
  listaMarketOverrides?: ListaMarketOverrides,
  stalenessThresholdSeconds = 3600,
  onlyFetchers?: string[],
  probeFeedStaleness = true,
  /**
   * Curve LlamaLend markets. Appended LAST rather than slotted next to the
   * other two override params on purpose — ~50 call sites already pass all 12
   * positional arguments, and inserting here would silently shift
   * `onlyFetchers` / `probeFeedStaleness` in every one of them.
   */
  llamaLendMarketOverrides?: LlamaLendMarketOverrides,
  /**
   * Omit to take the per-chain default from `morphoIncludesUnlisted` — the
   * SAME resolver `getLenderPublicDataAll` uses, so the price roster and the
   * market roster cannot disagree. Resolved PER CHAIN below, which a single
   * boolean could not do: this function takes `chainIds[]` while the market
   * fetch is one chain at a time. Pass a boolean only to override every chain
   * in this call. Appended last for the same reason
   * `llamaLendMarketOverrides` was.
   */
  includeUnlistedMorphoMarkets?: boolean,
): Promise<OraclePricesResult> {
  const totalStart = Date.now()
  const result: StructuredOraclePrices = {}

  // Process each chain
  const chainPromises = chainIds.map(async (chainId) => {
    const chainStart = Date.now()
    const chainResult: { [lender: string]: OraclePriceEntry[] } = {}
    const tokenList = lists[chainId] ?? {}
    const getCallsErrors: { [fetcher: string]: string } = {}
    const trackerDiags: TrackerDiagnostic[] = []
    const unresolvedMorphoMarkets: string[] = []
    const staleFeeds: StaleFeedEntry[] = []
    const multicallErrors: string[] = []

    // Collect all fetcher results with error isolation
    const active = onlyFetchers
      ? new Set(onlyFetchers.map((f) => f.toLowerCase()))
      : null
    const isActive = (name: string) => !active || active.has(name)

    // TermMax markets are DISCOVERED at runtime (no static registry — they roll
    // with every maturity), so `getTermMaxCalls` can only see what the lending
    // fetch has already cached. In a STANDALONE price run that cache is cold
    // and TermMax would contribute ZERO prices, silently — verified. Warm it
    // here: one API call per chain, last-known-good cached and shared with the
    // lending path, so it is a no-op when the lending fetch already ran.
    if (isActive('termmax') && termMaxConfigByChain(chainId)) {
      await fetchTermMaxMarkets(chainId).catch(() => [])
    }

    // Curvance has the same cold-cache problem for the same reason: its markets
    // are DISCOVERED on-chain, and `getCurvanceCalls` is synchronous, so in a
    // STANDALONE price run the market->assets roster is empty and Curvance would
    // contribute zero prices silently. Warm it here; a no-op when the lending
    // fetch already ran this cycle (the roster is cached and non-empty).
    if (isActive('curvance')) {
      for (const lender of curvanceLendersByChain(chainId)) {
        if (getCurvanceAssetRoster(chainId, lender).length > 0) continue
        await fetchCurvanceMarkets(lender, chainId).catch(() => undefined)
      }
    }

    // Resupply is the same shape as Curvance: its pairs are DISCOVERED from
    // the registry and `getResupplyCalls` is synchronous, so in a STANDALONE
    // price run the pair roster is empty and reUSD would silently get no price
    // at all — which is the bug this fetcher exists to fix. Warm it here; a
    // no-op when the lending fetch already ran this cycle.
    if (isActive('resupply')) {
      for (const lender of resupplyLendersByChain(chainId)) {
        if (getResupplyPairRoster(chainId, lender).length > 0) continue
        await fetchResupplyMarkets(lender, chainId).catch(() => undefined)
      }
    }

    const aaveResults = isActive('aave')
      ? safeGetCalls(
          'aave',
          () => aaveFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const compoundV2Results = isActive('compoundv2')
      ? safeGetCalls(
          'compoundV2',
          () => compoundV2Fetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const compoundV3Results = isActive('compoundv3')
      ? safeGetCalls(
          'compoundV3',
          () => compoundV3Fetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const listaResults = isActive('lista')
      ? safeGetCalls(
          'lista',
          () =>
            listaFetcher.getCalls(chainId, {
              marketOverrides: listaMarketOverrides,
            }),
          getCallsErrors,
        )
      : []
    const llamaLendResults = isActive('llamalend')
      ? safeGetCalls(
          'llamaLend',
          () =>
            llamaLendFetcher.getCalls(chainId, {
              marketOverrides: llamaLendMarketOverrides,
            }),
          getCallsErrors,
        )
      : []
    const eulerResults = isActive('eulerv2')
      ? safeGetCalls(
          'eulerV2',
          () => eulerV2Fetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const aaveV4Results = isActive('aavev4')
      ? safeGetCalls(
          'aaveV4',
          () => aaveV4Fetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    // Classification for Morpho:
    //   - morphoUseMystic: Mystic API chain (Flare, Plume, Citrea) WITH a
    //     configured key — the official Morpho API doesn't index these forks,
    //     so we reshape Mystic's morphoCache into the same per-market entries.
    //     Without a key these chains are on-chain-only, which is a COMPLETE
    //     source, not a degraded one: the on-chain roster covers them and
    //     carries the oracle ratio Mystic never returns.
    //   - morphoUseApi: chain indexed by the official Morpho GraphQL API.
    //   - morphoUseHybrid: API/Mystic remote runs PLUS the on-chain multicall
    //     (chains flagged as `morphoApiIncomplete` — e.g. Mystic chains miss
    //     the on-chain oracle ratio, so we run on-chain to get it). On-chain
    //     entries win where both produce the same (marketUid, asset); remote
    //     fills any market the on-chain overrides don't cover.
    //   - morphoUseOnChain: on-chain multicall — runs when there's no remote
    //     source, OR alongside the remote in hybrid mode.
    const morphoUseMystic = isActive('morpho') && mysticPricesAvailable(chainId)
    const morphoUseApi =
      isActive('morpho') && !morphoUseMystic && morphoApiAvailable(chainId)
    const morphoUseRemote = morphoUseMystic || morphoUseApi
    const morphoUseHybrid = morphoUseRemote && morphoApiIncomplete(chainId)
    const morphoUseOnChain =
      isActive('morpho') && (!morphoUseRemote || morphoUseHybrid)
    const morphoResults = morphoUseOnChain
      ? safeGetCalls(
          'morpho',
          () =>
            morphoFetcher.getCalls(chainId, {
              marketOverrides: morphoMarketOverrides,
            }),
          getCallsErrors,
        )
      : []
    // Midnight: always on-chain (no remote price API). One `price()` per
    // collateral leg, keyed to the per-market `MORPHO_MIDNIGHT_<id>` lender.
    const midnightResults = isActive('midnight')
      ? safeGetCalls(
          'midnight',
          () => midnightFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    // Exactly: always on-chain, self-sourced from config (no override map).
    // ONE `Previewer.exactly(0x0)` returns every market's direct USD price,
    // keyed to the per-market `EXACTLY_<market>` lender.
    const exactlyResults = isActive('exactly')
      ? safeGetCalls(
          'exactly',
          () => exactlyFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    // Flying Tulip: always on-chain, self-sourced from config. ONE
    // `priceAndDecimals` call PER priceable asset (never the batched
    // `pricesUSD` — one unpriceable asset reverts the whole batch, and FT
    // itself is unpriceable on both chains), keyed to the single per-chain
    // `FLYING_TULIP` lender.
    const flyingTulipResults = isActive('flyingTulip')
      ? safeGetCalls(
          'flyingTulip',
          () => flyingTulipFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    // Term Finance: collateral + loan token prices from Term's OWN oracle
    // (TermPriceConsumerV3.usdValueOfTokens), deduped per unique token, keyed
    // to each repo's `TERM_FINANCE_<id>` lender. The oracle Term liquidates
    // against → correct liquidation-risk pricing.
    const termResults = isActive('term')
      ? safeGetCalls(
          'term',
          () => termFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    // Liquity family + River: on-chain, self-sourced from config. One collateral
    // USD read per branch/market (`lastGoodPrice` / `fetchPrice`), keyed to the
    // per-branch `<DEPLOYMENT>_<chain>_<idx>` lender. Prices exotic collateral no
    // other chain protocol covers (e.g. Enosys on Flare, Felix on HyperEVM).
    const liquityResults = isActive('liquity')
      ? safeGetCalls(
          'liquity',
          () => liquityFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const riverResults = isActive('river')
      ? safeGetCalls(
          'river',
          () => riverFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    // dss (Sky + USDD): Maker's OSM price recovered from Vat.spot × mat.
    // Feeds the per-market ORACLE channel so health factors match the
    // protocol's own liquidation condition — see fetchers/dss.ts.
    const dssResults = isActive('dss')
      ? safeGetCalls('dss', () => dssFetcher.getCalls(chainId), getCallsErrors)
      : []
    const curvanceResults = isActive('curvance')
      ? safeGetCalls(
          'curvance',
          () => curvanceFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const resupplyResults = isActive('resupply')
      ? safeGetCalls(
          'resupply',
          () => resupplyFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const inverseResults = isActive('inverse')
      ? safeGetCalls(
          'inverse',
          () => inverseFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const tellerResults = isActive('teller')
      ? safeGetCalls(
          'teller',
          () => tellerFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const fraxlendResults = isActive('fraxlend')
      ? safeGetCalls(
          'fraxlend',
          () => fraxlendFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const termMaxResults = isActive('termmax')
      ? safeGetCalls(
          'termmax',
          () => termMaxFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const siloV2Results = isActive('silov2')
      ? safeGetCalls(
          'siloV2',
          () => siloV2Fetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const siloV3Results = isActive('silov3')
      ? safeGetCalls(
          'siloV3',
          () => siloV3Fetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const fluidResults = isActive('fluid')
      ? safeGetCalls(
          'fluid',
          () => fluidFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const gearboxV3Results = isActive('gearboxV3')
      ? safeGetCalls(
          'gearboxV3',
          () => gearboxV3Fetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []
    const dolomiteResults = isActive('dolomite')
      ? safeGetCalls(
          'dolomite',
          () => dolomiteFetcher.getCalls(chainId),
          getCallsErrors,
        )
      : []

    // Build per-fetcher groups — each gets its own multicall
    const aaveGroup = buildGroup(
      'aave',
      aaveResults,
      aaveFetcher.parse,
      AaveOracleAbi,
      'direct',
      getCallsErrors,
    )
    const compoundV2Group = buildGroup(
      'compoundV2',
      compoundV2Results,
      compoundV2Fetcher.parse,
      CompoundV2OracleAbi,
      'direct',
      getCallsErrors,
    )
    const compoundV3Group = buildGroup(
      'compoundV3',
      compoundV3Results,
      compoundV3Fetcher.parse,
      ChainlinkAggregatorAbi,
      'direct',
      getCallsErrors,
    )
    const listaGroup = buildGroup(
      'lista',
      listaResults,
      listaFetcher.parse,
      ProxyOracleAbi,
      'direct',
      getCallsErrors,
    )
    // DERIVED: `price_oracle()` returns the collateral priced in the BORROWED
    // token (WAD), so the USD leg is `ratio × borrowedUSD` — the Morpho shape.
    // Parsed in Pass 2 so the borrowed token's direct USD price exists first.
    const llamaLendGroup = buildGroup(
      'llamaLend',
      llamaLendResults,
      llamaLendFetcher.parse,
      getLlamaLendAbi(),
      'derived',
      getCallsErrors,
    )
    const eulerGroup = buildGroup(
      'eulerV2',
      eulerResults,
      eulerV2Fetcher.parse,
      getEulerV2Abi(),
      'direct',
      getCallsErrors,
    )
    const aaveV4Group = buildGroup(
      'aaveV4',
      aaveV4Results,
      aaveV4Fetcher.parse,
      getAaveV4Abi(),
      'direct',
      getCallsErrors,
    )
    const morphoGroup = buildGroup(
      'morpho',
      morphoResults,
      morphoFetcher.parse,
      ProxyOracleAbi,
      'derived',
      getCallsErrors,
    )
    const midnightGroup = buildGroup(
      'midnight',
      midnightResults,
      midnightFetcher.parse,
      ProxyOracleAbi,
      'derived',
      getCallsErrors,
    )
    const exactlyGroup = buildGroup(
      'exactly',
      exactlyResults,
      exactlyFetcher.parse,
      getExactlyAbi(),
      'direct',
      getCallsErrors,
    )
    const flyingTulipGroup = buildGroup(
      'flyingTulip',
      flyingTulipResults,
      flyingTulipFetcher.parse,
      getFlyingTulipAbi(),
      'direct',
      getCallsErrors,
    )
    const termGroup = buildGroup(
      'term',
      termResults,
      termFetcher.parse,
      getTermAbi(),
      'direct',
      getCallsErrors,
    )
    const liquityGroup = buildGroup(
      'liquity',
      liquityResults,
      liquityFetcher.parse,
      getLiquityAbi(),
      'direct',
      getCallsErrors,
    )
    const riverGroup = buildGroup(
      'river',
      riverResults,
      riverFetcher.parse,
      getRiverAbi(),
      'direct',
      getCallsErrors,
    )
    const dssGroup = buildGroup(
      'dss',
      dssResults,
      dssFetcher.parse,
      dssFetcher.getAbi(),
      'direct',
      getCallsErrors,
    )
    const curvanceGroup = buildGroup(
      'curvance',
      curvanceResults,
      curvanceFetcher.parse,
      curvanceFetcher.getAbi(),
      'direct',
      getCallsErrors,
    )
    const resupplyGroup = buildGroup(
      'resupply',
      resupplyResults,
      resupplyFetcher.parse,
      resupplyFetcher.getAbi(),
      'direct',
      getCallsErrors,
    )
    const inverseGroup = buildGroup(
      'inverse',
      inverseResults,
      inverseFetcher.parse,
      getInverseAbi(),
      'direct',
      getCallsErrors,
    )
    const tellerGroup = buildGroup(
      'teller',
      tellerResults,
      tellerFetcher.parse,
      getTellerAbi(),
      'derived',
      getCallsErrors,
    )
    const fraxlendGroup = buildGroup(
      'fraxlend',
      fraxlendResults,
      fraxlendFetcher.parse,
      getFraxlendAbi(),
      'derived',
      getCallsErrors,
    )
    const termMaxGroup = buildGroup(
      'termmax',
      termMaxResults,
      termMaxFetcher.parse,
      getTermMaxAbi(),
      // DIRECT: `OracleAggregatorV2.getPrice` returns a USD price outright —
      // nothing is derived from another asset's price.
      'direct',
      getCallsErrors,
    )
    const siloV2Group = buildGroup(
      'siloV2',
      siloV2Results,
      siloV2Fetcher.parse,
      getSiloV2Abi(),
      'derived',
      getCallsErrors,
    )
    const siloV3Group = buildGroup(
      'siloV3',
      siloV3Results,
      siloV3Fetcher.parse,
      getSiloV3Abi(),
      'derived',
      getCallsErrors,
    )
    const fluidGroup = buildGroup(
      'fluid',
      fluidResults,
      fluidFetcher.parse,
      getFluidAbi(),
      'derived',
      getCallsErrors,
    )
    const gearboxV3Group = buildGroup(
      'gearboxV3',
      gearboxV3Results,
      gearboxV3Fetcher.parse,
      getGearboxV3Abi(),
      'derived',
      getCallsErrors,
    )
    const dolomiteGroup = buildGroup(
      'dolomite',
      dolomiteResults,
      dolomiteFetcher.parse,
      getDolomiteAbi(),
      'direct',
      getCallsErrors,
    )

    const allGroups = [
      aaveGroup,
      compoundV2Group,
      compoundV3Group,
      listaGroup,
      llamaLendGroup,
      eulerGroup,
      aaveV4Group,
      morphoGroup,
      midnightGroup,
      exactlyGroup,
      flyingTulipGroup,
      termGroup,
      liquityGroup,
      riverGroup,
      dssGroup,
      curvanceGroup,
      resupplyGroup,
      inverseGroup,
      tellerGroup,
      fraxlendGroup,
      termMaxGroup,
      siloV2Group,
      siloV3Group,
      fluidGroup,
      gearboxV3Group,
      dolomiteGroup,
    ]

    const totalCalls = allGroups.reduce((s, g) => s + g.calls.length, 0)
    if (totalCalls === 0 && !isActive('morpho')) {
      const diag: ChainDiagnostic = {
        chainId,
        totalCalls: 0,
        totalFailedCalls: 0,
        totalParsedEntries: 0,
        durationMs: Date.now() - chainStart,
        trackers: [],
        getCallsErrors,
        unresolvedMorphoMarkets: [],
        staleFeeds: [],
      }
      return { chainId, data: chainResult, diagnostic: diag }
    }

    // Fire all multicalls + Morpho GraphQL in parallel.
    // GraphQL provides Morpho prices without on-chain calls; if it succeeds,
    // the on-chain morpho multicall is skipped entirely.
    const chainBatchSize = batchSize?.[chainId]

    // GraphQL/Mystic runs only on remote-available chains. On-chain path runs
    // only on chains without a remote source. No cross-fallback: if the remote
    // call fails, Morpho prices are simply skipped for this cycle.
    const morphoGqlPromise = morphoUseMystic
      ? fetchMorphoMysticPrices(chainId)
      : morphoUseApi
        ? fetchMorphoGraphQLPrices(
            chainId,
            includeUnlistedMorphoMarkets ?? morphoIncludesUnlisted(chainId),
          )
        : Promise.resolve(null)

    // Silo v2/v3 GraphQL prefetch — mirrors the Morpho path: returns
    // parsed `OraclePriceEntry[]` directly (or `null` to fall back to the
    // on-chain multicall). Self-contained — uses `supplyUsd / supply`
    // ratios from the same response (overlaid with `basePrices`) to
    // resolve quote-token USD without depending on other lender parsers.
    const siloV2GqlPromise = isActive('silov2')
      ? fetchSiloV2GraphQLPrices(chainId, basePrices, tokenList)
      : Promise.resolve(null)
    const siloV3GqlPromise = isActive('silov3')
      ? fetchSiloV3GraphQLPrices(chainId, basePrices, tokenList)
      : Promise.resolve(null)

    // AAVE feed-timestamp probe — runs alongside the price groups so its two
    // extra round trips cost wall-clock only when they outlast the prices.
    // Awaited after parsing, where the timestamps are attached to entries.
    const feedTimestampsPromise: Promise<FeedTimestampMap> =
      stalenessThresholdSeconds > 0 && probeFeedStaleness
        ? probeAaveFeedTimestamps(chainId, {
            rpcOverrides,
            batchSize: chainBatchSize,
            retries,
          })
        : Promise.resolve({})

    const [
      aaveData,
      compoundV2Data,
      compoundV3Data,
      listaData,
      llamaLendData,
      eulerData,
      aaveV4Data,
      fluidData,
      gearboxV3Data,
      dolomiteData,
      midnightData,
      exactlyData,
      flyingTulipData,
      termData,
      liquityData,
      riverData,
      dssData,
      curvanceData,
      resupplyData,
      inverseData,
      tellerData,
      fraxlendData,
      termMaxData,
      morphoGqlEntries,
      siloV2GqlEntries,
      siloV3GqlEntries,
    ] = await Promise.all([
      executeGroup(
        aaveGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        compoundV2Group,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        compoundV3Group,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        listaGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        llamaLendGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        eulerGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        aaveV4Group,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        fluidGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        gearboxV3Group,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        dolomiteGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        midnightGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        exactlyGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        flyingTulipGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        termGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        liquityGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        riverGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        dssGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        curvanceGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        resupplyGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        inverseGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        tellerGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        fraxlendGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      executeGroup(
        termMaxGroup,
        chainId,
        chainBatchSize,
        retries,
        allowFailure,
        rpcOverrides,
      ),
      morphoGqlPromise,
      siloV2GqlPromise,
      siloV3GqlPromise,
    ])

    // Silo v2 on-chain multicall: only run when GraphQL prefetch returned
    // nothing for this chain (mirrors the Morpho GraphQL-first pattern).
    if (
      siloV2GqlEntries == null &&
      siloV2Group.calls.length > 0 &&
      // Chains the indexer does not cover return null on EVERY run; the
      // on-chain path is their normal source, not a degraded one.
      SILO_API_SUPPORTED_CHAIN_IDS.has(chainId)
    ) {
      console.warn(
        `[prices] chain ${chainId}: Silo v2 GraphQL returned null, falling back to on-chain (${siloV2Group.trackers.length} trackers)`,
      )
    }
    const siloV2Data =
      siloV2GqlEntries != null
        ? { results: [], error: undefined }
        : await executeGroup(
            siloV2Group,
            chainId,
            chainBatchSize,
            retries,
            allowFailure,
            rpcOverrides,
          )

    // Same GraphQL-first pattern for Silo v3.
    if (
      siloV3GqlEntries == null &&
      siloV3Group.calls.length > 0 &&
      // Chains the indexer does not cover return null on EVERY run; the
      // on-chain path is their normal source, not a degraded one.
      SILO_API_SUPPORTED_CHAIN_IDS.has(chainId)
    ) {
      console.warn(
        `[prices] chain ${chainId}: Silo v3 GraphQL returned null, falling back to on-chain (${siloV3Group.trackers.length} trackers)`,
      )
    }
    const siloV3Data =
      siloV3GqlEntries != null
        ? { results: [], error: undefined }
        : await executeGroup(
            siloV3Group,
            chainId,
            chainBatchSize,
            retries,
            allowFailure,
            rpcOverrides,
          )

    // On-chain multicall runs whenever `morphoUseOnChain` is set:
    //   - Remote-unavailable chain → on-chain is the only source.
    //   - Hybrid chain → on-chain runs alongside the remote; merging happens
    //     below.
    const morphoData = morphoUseOnChain
      ? await executeGroup(
          morphoGroup,
          chainId,
          chainBatchSize,
          retries,
          allowFailure,
          rpcOverrides,
        )
      : { results: [], error: undefined }

    const useMorphoGql = morphoGqlEntries != null
    const useMorphoHybrid = useMorphoGql && morphoUseHybrid

    // Collect multicall errors (skip morpho if GraphQL succeeded)
    const groupResults = [
      { group: aaveGroup, data: aaveData },
      { group: compoundV2Group, data: compoundV2Data },
      { group: compoundV3Group, data: compoundV3Data },
      { group: listaGroup, data: listaData },
      { group: llamaLendGroup, data: llamaLendData },
      { group: eulerGroup, data: eulerData },
      { group: aaveV4Group, data: aaveV4Data },
      { group: fluidGroup, data: fluidData },
      { group: gearboxV3Group, data: gearboxV3Data },
      { group: dolomiteGroup, data: dolomiteData },
      { group: midnightGroup, data: midnightData },
      { group: tellerGroup, data: tellerData },
      { group: fraxlendGroup, data: fraxlendData },
      { group: termMaxGroup, data: termMaxData },
      ...(siloV2GqlEntries != null
        ? []
        : [{ group: siloV2Group, data: siloV2Data }]),
      ...(siloV3GqlEntries != null
        ? []
        : [{ group: siloV3Group, data: siloV3Data }]),
      // In hybrid mode the on-chain multicall ran too — surface its errors.
      ...(useMorphoGql && !useMorphoHybrid
        ? []
        : [{ group: morphoGroup, data: morphoData }]),
    ]
    for (const { group, data } of groupResults) {
      if (data.error) {
        multicallErrors.push(`[${group.fetcherName}] ${data.error}`)
      }
    }

    // Build USD price map from base prices and parsed results
    const usdPrices: USDPriceMap = { ...basePrices }

    /**
     * Parse trackers in a group using its multicall results.
     * When updatePrices is true, parsed USD prices feed back into
     * the shared usdPrices map so subsequent parsers can use them.
     * Optional filter predicate selects a subset of trackers.
     */
    const parseTrackers = (
      group: FetcherGroup,
      data: any[],
      updatePrices = true,
      filter?: (tracker: FetcherGroup['trackers'][number]) => boolean,
    ) => {
      const trackers = filter ? group.trackers.filter(filter) : group.trackers

      for (const tracker of trackers) {
        const failedCalls = countFailures(data, tracker.offset, tracker.count)

        const diag: TrackerDiagnostic = {
          lender: tracker.lender,
          callCount: tracker.count,
          failedCalls,
          parsedEntries: 0,
        }

        try {
          const dataSlice = data.slice(
            tracker.offset,
            tracker.offset + tracker.count,
          )

          const context: ParseContext = { chainId, usdPrices, tokenList }
          const entries = tracker.parse(dataSlice, tracker.meta, context)
          diag.parsedEntries = entries.length

          for (const entry of entries) {
            const lender = entry.targetLender ?? tracker.lender
            if (!chainResult[lender]) {
              chainResult[lender] = []
            }
            // Stamp the quality class before the entry leaves the pipeline —
            // a parser that set one per entry keeps it.
            entry.derivation = resolveDerivation(entry, group.derivation)
            chainResult[lender].push(entry)

            if (updatePrices) {
              const oracleKey =
                tokenList[entry.asset]?.assetGroup ??
                `${chainId}-${entry.asset}`
              usdPrices[oracleKey] = entry.priceUSD
              usdPrices[entry.asset] = entry.priceUSD
            }
          }
        } catch (e) {
          diag.parseError = e instanceof Error ? e.message : String(e)
        }

        trackerDiags.push(diag)
      }
    }

    // ── Parse in dependency order ──────────────────────────────────────

    // 1a. AAVE sources first (no baseAssetSource) — establish core prices
    parseTrackers(
      aaveGroup,
      aaveData.results,
      true,
      (t) => !t.meta.baseAssetSource,
    )

    // 1b. Other protocols (CompoundV2, CompoundV3, Lista, Euler)
    parseTrackers(compoundV2Group, compoundV2Data.results)
    parseTrackers(compoundV3Group, compoundV3Data.results)
    parseTrackers(listaGroup, listaData.results)
    parseTrackers(eulerGroup, eulerData.results)
    parseTrackers(aaveV4Group, aaveV4Data.results)
    // TermMax `OracleAggregatorV2.getPrice` returns a USD price outright, so it
    // belongs in Pass 1 with the other DIRECT reads — not in the derived pass
    // with Morpho/Midnight/Teller. It is the feed TermMax itself liquidates
    // against, so it is authoritative for that market's risk even when the
    // generic pipeline already prices the token.
    parseTrackers(termMaxGroup, termMaxData.results)

    // 1c. AAVE dependents (need base asset USD from sources)
    parseTrackers(
      aaveGroup,
      aaveData.results,
      true,
      (t) => !!t.meta.baseAssetSource,
    )

    // 1c.2 Fluid — per-vault entries derive collateral USD from loan-asset
    // USD (base is the loan side). Run after aave/compound so the loan
    // asset's USD price is already in `usdPrices`. Derived prices DO feed
    // back (set `updatePrices = false` if downstream shouldn't trust
    // Fluid's collateral pricing).
    parseTrackers(fluidGroup, fluidData.results, false)

    // Gearbox V3 — per-CreditManager entries. Each CM emits one
    // underlying + one price per allowed collateral token. Prices resolve
    // from the accumulated `usdPrices` map (populated by Aave / Compound
    // base prices above). No base-token dependency.
    parseTrackers(gearboxV3Group, gearboxV3Data.results, false)
    // Dolomite prices are self-denominated USD → feed the shared map (default true)
    parseTrackers(dolomiteGroup, dolomiteData.results)
    // Exactly prices are direct USD (Previewer.usdPrice, 1e18) → feed the
    // shared map (default true). Prices assets no other protocol on the chain
    // covers (e.g. cbXRP on Base) from Exactly's own oracle.
    parseTrackers(exactlyGroup, exactlyData.results)
    // Flying Tulip — direct USD from the protocol's own OracleRouter via the
    // LendingLens (priceAndDecimals, 1e18). Feed the shared map (default
    // true) so FT collateral is priced by the oracle it liquidates against.
    parseTrackers(flyingTulipGroup, flyingTulipData.results)
    // Term Finance — direct USD from Term's own oracle (usdValueOfTokens,
    // 1e18). Feed the shared map (default true) so Term collateral is priced
    // by the SAME oracle Term liquidates against.
    parseTrackers(termGroup, termData.results)
    // Liquity family + River — direct USD collateral prices (lastGoodPrice /
    // fetchPrice, 1e18) + pegged stablecoin. Feed the shared map (default true)
    // so exotic branch collateral on thin chains is priced from its own oracle.
    parseTrackers(liquityGroup, liquityData.results)
    parseTrackers(riverGroup, riverData.results)
    // dss (Sky / USDD) — Maker's OSM price recovered from Vat.spot × mat,
    // scoped to each ilk's market so the health factor matches the
    // protocol's own liquidation condition. Deliberately ~1h delayed: that
    // is the staleness the Dog/Clip engine itself acts on.
    parseTrackers(dssGroup, dssData.results)
    // Curvance — direct USD from the shared per-chain OracleManager, taken on
    // the PESSIMISTIC-LOW side (what the protocol values collateral with) and
    // dropped entirely on a non-zero oracle error code. Feeds the shared map so
    // Monad-native collateral no other protocol prices is covered.
    parseTrackers(curvanceGroup, curvanceData.results)
    // Resupply — reUSD at the protocol's OWN PriceWatcher feed (~0.99, NOT
    // par). Nothing else in the pipeline prices reUSD, and two unrelated
    // tokens share its ticker, so without this the qualified group resolves to
    // no price and every USD figure on the debt leg reads $0.
    parseTrackers(resupplyGroup, resupplyData.results)
    // Inverse FiRM — pessimistic-oracle collateral prices (viewPrice,
    // 10^(36−collDec)) + DOLA at par. Deliberately dampened (~2-day low);
    // this IS the price FiRM enforces limits with.
    parseTrackers(inverseGroup, inverseData.results)

    // 1d. Silo V2 — mirrors the Morpho block below: the GraphQL fetcher
    // already returned parsed `OraclePriceEntry[]` (self-contained, no
    // dependence on cross-protocol USD prices), so we just push them.
    // Falls back to the on-chain parser when GraphQL returned `null`.
    if (siloV2GqlEntries != null) {
      const diag: TrackerDiagnostic = {
        lender: 'SILO_V2 (GraphQL)',
        callCount: 0,
        failedCalls: 0,
        parsedEntries: siloV2GqlEntries.length,
      }
      for (const entry of siloV2GqlEntries) {
        const lender = entry.targetLender ?? 'SILO_V2'
        if (!chainResult[lender]) chainResult[lender] = []
        // Indexer USD derived from supplyUsd/supply ratios, not a feed read.
        entry.derivation = resolveDerivation(entry, 'derived')
        chainResult[lender].push(entry)
        const oracleKey =
          tokenList[entry.asset]?.assetGroup ?? `${chainId}-${entry.asset}`
        usdPrices[oracleKey] = entry.priceUSD
        usdPrices[entry.asset] = entry.priceUSD
      }
      trackerDiags.push(diag)
    } else {
      parseTrackers(siloV2Group, siloV2Data.results)
    }

    // 1e. Silo V3 — same GraphQL-first / on-chain-fallback pattern as v2.
    if (siloV3GqlEntries != null) {
      const diag: TrackerDiagnostic = {
        lender: 'SILO_V3 (GraphQL)',
        callCount: 0,
        failedCalls: 0,
        parsedEntries: siloV3GqlEntries.length,
      }
      for (const entry of siloV3GqlEntries) {
        const lender = entry.targetLender ?? 'SILO_V3'
        if (!chainResult[lender]) chainResult[lender] = []
        entry.derivation = resolveDerivation(entry, 'derived')
        chainResult[lender].push(entry)
        const oracleKey =
          tokenList[entry.asset]?.assetGroup ?? `${chainId}-${entry.asset}`
        usdPrices[oracleKey] = entry.priceUSD
        usdPrices[entry.asset] = entry.priceUSD
      }
      trackerDiags.push(diag)
    } else {
      parseTrackers(siloV3Group, siloV3Data.results)
    }

    // 2. Morpho — three modes:
    //    a) Remote only (useMorphoGql && !useMorphoHybrid): push API entries.
    //    b) Hybrid (useMorphoHybrid): seed usdPrices from API so the on-chain
    //       parser can resolve markets, run on-chain parser, then append API
    //       entries that didn't get covered by the on-chain pass.
    //    c) On-chain only: parse the multicall against overrides.
    if (useMorphoHybrid) {
      // Step 1: seed usdPrices with API USD so the on-chain parser can derive
      // collateral USD even when no other lender on this chain has set a base.
      for (const entry of morphoGqlEntries) {
        if (!(entry.priceUSD > 0)) continue
        const oracleKey =
          tokenList[entry.asset]?.assetGroup ?? `${chainId}-${entry.asset}`
        usdPrices[oracleKey] = entry.priceUSD
        usdPrices[entry.asset] = entry.priceUSD
      }

      // Step 2: run the on-chain parser. It pushes its entries into
      // chainResult and they carry the real on-chain oracle ratio.
      // We do not feed derived prices back (mirrors the on-chain-only path).
      parseTrackers(morphoGroup, morphoData.results, false)

      // Step 3: append API entries that the on-chain pass didn't cover.
      // Dedup key matches the natural identity of a Morpho price entry.
      const onChainKeys = new Set<string>()
      for (const lender of Object.keys(chainResult)) {
        for (const e of chainResult[lender]) {
          if (!e.targetLender) continue
          if (!e.targetLender.startsWith('MORPHO')) continue
          onChainKeys.add(`${e.targetLender}|${e.marketUid}|${e.asset}`)
        }
      }

      let gqlPushed = 0
      for (const entry of morphoGqlEntries) {
        const lender = entry.targetLender ?? 'MORPHO_BLUE'
        const key = `${lender}|${entry.marketUid}|${entry.asset}`
        if (onChainKeys.has(key)) continue
        if (!chainResult[lender]) chainResult[lender] = []
        // The remote fetcher stamps per side (API USD vs ratio-derived);
        // 'derived' is only the fallback for entries that carry neither.
        entry.derivation = resolveDerivation(entry, 'derived')
        chainResult[lender].push(entry)
        gqlPushed++
      }
      trackerDiags.push({
        lender: 'MORPHO_BLUE (hybrid-fill)',
        callCount: 0,
        failedCalls: 0,
        parsedEntries: gqlPushed,
      })
    } else if (useMorphoGql) {
      // GraphQL returned prices directly — add them to chain result
      const morphoGqlDiag: TrackerDiagnostic = {
        lender: 'MORPHO_BLUE (GraphQL)',
        callCount: 0,
        failedCalls: 0,
        parsedEntries: morphoGqlEntries.length,
      }
      for (const entry of morphoGqlEntries) {
        const lender = entry.targetLender ?? 'MORPHO_BLUE'
        if (!chainResult[lender]) chainResult[lender] = []
        entry.derivation = resolveDerivation(entry, 'derived')
        chainResult[lender].push(entry)
      }
      trackerDiags.push(morphoGqlDiag)
    } else {
      // On-chain fallback — derived prices, do NOT update shared usdPrices
      // Track unresolved markets first
      for (const tracker of morphoGroup.trackers) {
        const meta = tracker.meta as { markets?: any[] }
        if (meta.markets) {
          for (const market of meta.markets) {
            const loanAsset = market.loanAsset?.toLowerCase()
            const collateralAsset = market.collateralAsset?.toLowerCase()
            const loanOracleKey =
              tokenList[loanAsset]?.assetGroup ?? `${chainId}-${loanAsset}`
            const collateralOracleKey =
              tokenList[collateralAsset]?.assetGroup ??
              `${chainId}-${collateralAsset}`

            const hasLoanPrice =
              usdPrices[loanOracleKey] !== undefined ||
              usdPrices[loanAsset] !== undefined
            const hasCollateralPrice =
              usdPrices[collateralOracleKey] !== undefined ||
              usdPrices[collateralAsset] !== undefined

            if (!hasLoanPrice && !hasCollateralPrice) {
              unresolvedMorphoMarkets.push(
                `${market.oracle}:${loanAsset}/${collateralAsset}`,
              )
            }
          }
        }
      }
      parseTrackers(morphoGroup, morphoData.results, false)
    }

    // 2b. Morpho Midnight — same derived pricing as Morpho Blue (collateral USD
    // = oracle ratio * loan asset USD). Parsed after Pass 1 so the loan asset's
    // direct USD price is in `usdPrices`; `updatePrices=false` so the derived
    // collateral prices don't overwrite direct oracle readings.
    parseTrackers(midnightGroup, midnightData.results, false)

    // 2c. Teller — derived collateral pricing from each pool's OWN oracle
    // (collateral USD = value ratio * principal asset USD). Parsed after Pass 1
    // so the principal's direct USD is in `usdPrices`; `updatePrices=false` so
    // derived collateral prices don't overwrite direct oracle readings. Prices
    // exotic collateral (VCNT/LFI/…) no other protocol on the chain covers.
    parseTrackers(tellerGroup, tellerData.results, false)

    // 2c-bis. Fraxlend — derived collateral pricing from each PAIR's OWN
    // oracle, which is the feed the pair LIQUIDATES against, so publishing
    // anything else would put our health factors at odds with the chain.
    // Parsed after Pass 1 so the asset's direct USD is in `usdPrices`;
    // `updatePrices=false` so derived collateral prices don't overwrite direct
    // oracle readings. Prices collateral nothing else on the chain covers
    // (FXB bonds, srRoyUSDC, WFRAX).
    parseTrackers(fraxlendGroup, fraxlendData.results, false)

    // 2d. Curve LlamaLend — `AMM.price_oracle()` is the collateral priced in
    // the BORROWED token (WAD), so collateral USD = ratio * borrowed USD.
    // Parsed after Pass 1 so the borrowed token's direct USD is in `usdPrices`;
    // `updatePrices=false` so the derived collateral prices don't overwrite
    // direct oracle readings. Eight markets borrow something other than crvUSD,
    // so the base leg is load-bearing rather than a formality.
    parseTrackers(llamaLendGroup, llamaLendData.results, false)

    // ── Feed staleness ─────────────────────────────────────────────────
    // Runs over PARSED ENTRIES, not over one fetcher's raw results, so every
    // oracle that exposes a timestamp is covered — previously only Compound
    // V3 was checked, which meant zero coverage on chains without a Comet
    // deployment (Avalanche among them).
    if (stalenessThresholdSeconds > 0) {
      // Back-fill AAVE-family entries with their source feed's round data.
      const feedTimestamps = await feedTimestampsPromise
      for (const [lender, assetMap] of Object.entries(feedTimestamps)) {
        for (const entry of chainResult[lender] ?? []) {
          const info = assetMap[entry.asset]
          if (!info) continue
          entry.updatedAt = info.updatedAt
          entry.feedSource = info.source
          if (!entry.description && info.description) {
            entry.description = info.description
          }
        }
      }

      const nowSeconds = Math.floor(Date.now() / 1000)
      const reported = new Set<string>()
      for (const [lender, entries] of Object.entries(chainResult)) {
        for (const entry of entries) {
          if (!entry.updatedAt || entry.updatedAt <= 0) continue
          const staleSeconds = nowSeconds - entry.updatedAt
          if (staleSeconds <= stalenessThresholdSeconds) continue
          // One report per (lender, asset, feed) — Aave forks share feeds
          const key = `${lender}|${entry.asset}|${entry.feedSource ?? ''}`
          if (reported.has(key)) continue
          reported.add(key)
          staleFeeds.push({
            asset: entry.asset,
            lender,
            oracle: entry.feedSource ?? 'unknown',
            staleSeconds,
            description: entry.description,
          })
        }
      }
    }

    // ── Assemble diagnostics ───────────────────────────────────────────

    const totalFailedCalls = trackerDiags.reduce(
      (sum, d) => sum + d.failedCalls,
      0,
    )
    const totalParsedEntries = trackerDiags.reduce(
      (sum, d) => sum + d.parsedEntries,
      0,
    )

    const diag: ChainDiagnostic = {
      chainId,
      totalCalls: totalCalls,
      totalFailedCalls,
      totalParsedEntries,
      durationMs: Date.now() - chainStart,
      trackers: trackerDiags,
      getCallsErrors,
      chainError:
        multicallErrors.length > 0 ? multicallErrors.join('; ') : undefined,
      unresolvedMorphoMarkets,
      staleFeeds,
    }

    return { chainId, data: chainResult, diagnostic: diag }
  })

  // Wait for all chains with isolation — one failure does not kill others
  const settled = await Promise.allSettled(chainPromises)

  const chainDiagnostics: ChainDiagnostic[] = []
  const failedChains: string[] = []

  for (let i = 0; i < settled.length; i++) {
    const settlement = settled[i]
    const chainId = chainIds[i]

    if (settlement.status === 'fulfilled') {
      const { data, diagnostic } = settlement.value
      result[chainId] = data
      chainDiagnostics.push(diagnostic)
    } else {
      failedChains.push(chainId)
      chainDiagnostics.push({
        chainId,
        totalCalls: 0,
        totalFailedCalls: 0,
        totalParsedEntries: 0,
        durationMs: 0,
        trackers: [],
        getCallsErrors: {},
        chainError:
          settlement.reason instanceof Error
            ? settlement.reason.message
            : String(settlement.reason),
        unresolvedMorphoMarkets: [],
        staleFeeds: [],
      })
    }
  }

  const diagnostics: OracleDiagnostics = {
    chains: chainDiagnostics,
    totalDurationMs: Date.now() - totalStart,
    failedChains,
  }

  return { prices: result, diagnostics }
}
